import { useGetAccountEntitlements } from '@/api/accounts'
import { computed } from 'vue'
import { useStore } from 'vuex-composition-helpers'
import { useQuery } from '@tanstack/vue-query'
import { useLocalStorage } from '@vueuse/core'
import moment from 'moment'
import { getAdminPortalConfig } from '@/api/config'
import { useQboPromotionConfigQuery } from '@/api/composables/qbo-promotion'

const BULK_UPDATE_QBO_CALLOUT_DISMISS_STORAGE_KEY =
  'feature-promo:qbo-bulk-update-callout-dismissed'

export const usePromotionQuery = () => {
  const store = useStore()
  const isQboBulkUpdatePromotionEnabled = computed<boolean>(
    () => store.getters['featureFlags/qboBulkUpdatePromotion'],
  )
  const { data: currentOrgEntitlements, isSuccess: isEntitlementsQuerySuccess } =
    useGetAccountEntitlements()
  const { data: qboPromotionConfig } = useQboPromotionConfigQuery(isQboBulkUpdatePromotionEnabled)
  const dismissedByOrganization = useLocalStorage<Record<string, boolean>>(
    BULK_UPDATE_QBO_CALLOUT_DISMISS_STORAGE_KEY,
    {},
  )

  const { data: adminPortalConfig } = useQuery({
    // Keep this key aligned with other getAdminPortalConfig consumers so
    // TanStack Query dedupes requests instead of racing through shared cancel tokens.
    queryKey: ['config'],
    queryFn: getAdminPortalConfig,
    staleTime: moment.duration(1, 'days').asMilliseconds(),
  })

  const isAdminFor = computed<(organizationId: string | null) => boolean>(
    () => store.getters['permissions/isAdminFor'],
  )
  const resolvedEntitlements = computed(() =>
    isEntitlementsQuerySuccess.value ? (currentOrgEntitlements.value ?? null) : null,
  )
  const isEntitlementsResolved = computed(() => resolvedEntitlements.value !== null)

  const hasQboEntitlement = computed(() => {
    return resolvedEntitlements.value?.entitlements.includes('QboApp') ?? false
  })

  const isQboBulkUpdateCalloutDismissed = computed(() => {
    const organizationId = resolvedEntitlements.value?.organizationId
    if (!organizationId) {
      return false
    }

    return dismissedByOrganization.value?.[organizationId] === true
  })

  const dismissQboBulkUpdateCallout = () => {
    const organizationId = resolvedEntitlements.value?.organizationId
    if (!organizationId) {
      return
    }

    dismissedByOrganization.value = {
      ...dismissedByOrganization.value,
      [organizationId]: true,
    }
  }

  const canSeeQboPromotion = computed(() => {
    const entitlements = resolvedEntitlements.value

    return (
      !!entitlements &&
      entitlements.hasAccount &&
      hasQboEntitlement.value &&
      isAdminFor.value(entitlements.organizationId)
    )
  })

  const legacyQboAppSetupDeepLink = computed<string | undefined>(() => {
    const adminPortalUrl = adminPortalConfig.value?.data.url ?? ''
    const organizationId = resolvedEntitlements.value?.organizationId

    if (!adminPortalUrl || !organizationId) {
      return undefined
    }

    return `${adminPortalUrl}/organization/${organizationId}/apps/qbo`
  })

  const qboAppSetupDeepLink = computed<string | undefined>(() => {
    if (!isQboBulkUpdatePromotionEnabled.value) {
      return legacyQboAppSetupDeepLink.value
    }

    const marketingSetupUrl = qboPromotionConfig.value?.setupUrl?.trim()
    return marketingSetupUrl || legacyQboAppSetupDeepLink.value
  })

  return {
    isQboBulkUpdatePromotionEnabled,
    isEntitlementsResolved,
    hasQboEntitlement,
    isQboBulkUpdateCalloutDismissed,
    dismissQboBulkUpdateCallout,
    canSeeQboPromotion,
    qboAppSetupDeepLink,
  }
}
